import {
    RiAlertLine, RiDeleteBinLine, RiSettings4Fill,
    RiVerifiedBadgeLine,
} from "@remixicon/react";
import React, {
    ReactElement,
    useCallback,
    useState,
} from "react";
import {
    GetChannelEmailsDocument,
    GetSettingsDocument,
    SettingsChannelEmailAlias,
    useAddEmailAliasMutation, useDeleteEmailAliasMutation, useGetWorkflowByReferenceLazyQuery, useSaveSettingsMutation,
    WorkflowTriggerType,
} from "../../../../../__generated__/api";
import Editor from "../../../../../components/agent-v3/editor/Editor";
import CodeBlock from "../../../../../components/code-block/CodeBlock";
import { useFormBuilder } from "../../../../../components/dirty-tracker/FormTracker";
import SettingsContainer from "../../../../../components/theme/settings";
import useArticleContext from "../../../../../contexts/HelpContext";
import { SettingsProps } from "../../base";

import useDeleteConfirm from "../../../../../components/confirm/Delete";
import { Filters as FiltersSelector } from "../../../../../components/filters/Filters";
import Badge from "../../../../../components/theme/badge/Badge";
import Button from "../../../../../components/theme/button/Button";
import { useDrawerForm } from "../../../../../components/theme/drawer";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import createTableContainer from "../../../../../components/theme/table";
import Text from "../../../../../components/theme/text/Text";
import { LoggingHandler } from "../../../../../contexts/logging/LoggingContext";
import { NavLink, Paths } from "../../../../../contexts/Routing";
import Styles from "./Email.module.scss";
import ViewAutomation from "../../request-form/ViewAutomation";
import { AutomationTab } from "../../../../../components/workflow-v3/providers/WorkflowModalProvider";

const TableContainer = createTableContainer<SettingsChannelEmailAlias>();

export function useViewEmail() {
    const [save] = useAddEmailAliasMutation();

    const { element, setOpen, formTracker } = useDrawerForm({
        title: "Add Email Alias",
        onHide: () => {
            formTracker.reset();
        },
        onSave: async () => {
            await save({
                refetchQueries: [
                    { query: GetSettingsDocument },
                    { query: GetChannelEmailsDocument },
                ],

                variables: {
                    email: formTracker.properties.email.value,
                },
            });
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder.property("email", () => ""),
            dependencies: [],
        },
        body: tracker => (
            <>
                <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                    The email address you want to setup as a alias. Once registered, validated,
                    and after email forwarding is configured, any email sent to the one
                    above will forward to Agent and notify your team.
                </Text>
                <FormGroup
                    label="Email"
                    size="medium"
                    required
                    type="email"
                    placeholder="support@example.com"
                    value={tracker.properties.email.value}
                    onChange={evt => tracker.properties.email.setValue(evt.target.value)}
                />
            </>
        ),
    });

    return {
        element,
        setOpen,
    };
}

export default function EmailSettings(props: SettingsProps): ReactElement {
    useArticleContext("urn:fq:article:aec1306fe20e498590f512e76c2cf74e");

    const [saveFooter] = useSaveSettingsMutation();
    const [deleteAlias] = useDeleteEmailAliasMutation();
    const [loadReference] = useGetWorkflowByReferenceLazyQuery();

    const [viewWorkflowId, setViewWorkflowId] = useState<string | undefined>(undefined);

    const { element: deleteElement, setDeleting } = useDeleteConfirm<SettingsChannelEmailAlias>(
        async item => {
            await deleteAlias({
                refetchQueries: [
                    { query: GetSettingsDocument },
                    { query: GetChannelEmailsDocument },
                ],
                variables: {
                    id: item.id,
                },
            });
        },
        item => item.originalDestinationEmail,
        "Remove Email",
        "Once an email alias has been removed, you will no "
        + "longer be able to receive emails sent to this address. "
        + "Are you sure you want to delete",
        "Remove",
    );

    const { tracker, properties } = useFormBuilder(
        builder => builder.property("footer", () => props.settings?.channelEmail?.footer ?? ""),
        [props.settings?.channelEmail],
    );

    const defaultEmail = props.settings?.channelEmail?.emails.find(email => email.isDefault);
    // const nonDefault = emails;// .filter(email => !email.isDefault);

    const { element: drawerElement, setOpen } = useViewEmail();

    const performSave = useCallback(async () => {
        await LoggingHandler.instance.wrap(async () => {
            await saveFooter({
                variables: {
                    channelEmail: {
                        footer: properties.footer.value,
                    },
                },
            });
        });
    }, [properties.footer.value]);

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "Email settings saved!",
        onSave: async (__, onDone) => {
            await performSave();
            onDone();
        },
    });

    const handleShowAutomation = useCallback(async (item: SettingsChannelEmailAlias) => {
        const { data } = await loadReference({
            variables: {
                referenceId: item.id,
            },
        });
        setViewWorkflowId(data?.workflowReference?.workflow?.id);
    }, [loadReference]);

    return (
        <>
            {drawerElement}
            <SettingsContainer>
                <SettingsContainer.Section
                    title="General"
                    helpText="The general settings will help you get started with sending emails and receiving emails."
                >
                    <SettingsContainer.Setting
                        title="Your forwarding address"
                        description={`This is an automatically generated email for your email domain.`
                            + `When an email is sent to ${defaultEmail?.originalDestinationEmail}, the email`
                            + `will be automatically picked up by Agent and you will be notified.`
                            + `If you want to setup your email Alias (for example support@example.com),`
                            + `create a new email alias below and validate it.`}
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <CodeBlock shouldWrap code={defaultEmail?.originalDestinationEmail as string} size="sm" />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Footer"
                        description="This message will automatically be added to the bottom of each email."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <Editor
                            value={properties.footer.value ?? ""}
                            size="medium"
                            hasBorder
                            onChange={message => properties.footer.setValue(message)}
                            placeholder="Powered by Foqal."
                        />
                    </SettingsContainer.Setting>
                </SettingsContainer.Section>
                <SettingsContainer.Section
                    title="Email Aliases"
                    helpText="Email aliases allow you to set up custom rules for
                    routing emails, sending feedback, and running automation."
                >
                    <SettingsContainer.Setting
                        title="Email Aliases"
                        description="Email aliases allow you to set up custom rules for
                    routing emails, sending feedback, and running automation."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isHideTitle
                    >
                        <TableContainer
                            data={props.settings?.channelEmail?.emails ?? []}
                            isLoading={props.isLoading}
                            borderVariant="secondary"
                        >
                            <TableContainer.TableControls>
                                <TableContainer.TableControls.Search
                                    search={(query, item) => item.originalDestinationEmail?.toLocaleLowerCase().includes(query)}
                                />
                                <TableContainer.TableControls.Seperator />
                                <TableContainer.TableControls.Filters />
                                <TableContainer.TableControls.Filter
                                    defaultValue={undefined as boolean | undefined}
                                    deserialize={TableContainer.TableControls.Filter.Serializers.deserializeString<boolean>}
                                    serialize={TableContainer.TableControls.Filter.Serializers.serializeString<boolean>}
                                    filterKey="verified"
                                    onFilter={(item, selected) => (item.verifiedDate !== null || item.isDefault) === selected}
                                >
                                    {(currentValue, setValue) => (
                                        <FiltersSelector
                                            toggleText="Verified"
                                            className=""
                                            isMulti={false}
                                            icon={<RiVerifiedBadgeLine />}
                                            categories={[
                                                {
                                                    value: "true",
                                                    label: "Verified",
                                                },
                                                {
                                                    value: "false",
                                                    label: "Not Verified",
                                                },
                                            ]}
                                            selectedCategories={currentValue?.toString()}
                                            onSelected={value => setValue((value ? value === "true" : undefined) as boolean)}
                                        />
                                    )}

                                </TableContainer.TableControls.Filter>

                                <TableContainer.TableControls.SortBy />
                                <TableContainer.TableControls.Create
                                    text="Add Email Alias"
                                    variant="outline-blue"
                                    onClick={() => {
                                        setOpen(true);
                                    }}
                                />
                            </TableContainer.TableControls>
                            <TableContainer.Table>
                                <TableContainer.TableValue
                                    columnKey="email"
                                    title="Email"
                                    sortBy={item => item.originalDestinationEmail?.toLowerCase()}
                                >
                                    {item => item.originalDestinationEmail}
                                </TableContainer.TableValue>
                                <TableContainer.TableValue
                                    columnKey="status"
                                    title="Status"
                                    sortBy={item => item.verifiedDate != null || item.isDefault}
                                >
                                    {item => (item.verifiedDate != null || item.isDefault
                                        ? (
                                            <Badge variant="success" prefixIcon={<RiVerifiedBadgeLine />}>
                                                Verified
                                            </Badge>
                                        )
                                        : (
                                            <Badge variant="danger" prefixIcon={<RiAlertLine />}>
                                                Not Verified
                                            </Badge>
                                        ))}
                                </TableContainer.TableValue>

                                <TableContainer.TableValue
                                    columnKey="workflow"
                                    title="Settings"
                                >
                                    {item => (
                                        <NavLink
                                            to={Paths.ADMINISTRATOR_SETTING_ID}
                                            params={{
                                                setting: "chat",
                                                id: item.id,
                                            }}
                                            variant="none"
                                            isButton
                                            className={Styles["workflow-button"]}
                                            title={`Workflow for ${item.originalDestinationEmail}`}
                                        >
                                            <RiSettings4Fill />
                                            {" "}
                                            Edit Email Settings
                                        </NavLink>
                                    )}
                                </TableContainer.TableValue>

                                <TableContainer.TableValue
                                    columnKey="workflow"
                                    title="Workflow"
                                >
                                    {item => (
                                        !item.isDefault && (
                                            <Button.Automation
                                                size="small"
                                                variant="ghost-blue"
                                                onClick={() => handleShowAutomation(item)}
                                            >
                                                Edit Workflow
                                            </Button.Automation>
                                            // <WorkflowPicker
                                            //     referenceId={item.id.id}
                                            //     buttonText="Edit workflow"
                                            //     buttonClassName={Styles["workflow-button"]}
                                            //     title={`Workflow for ${item.originalDestinationEmail}`}
                                            // />
                                        )
                                    )}
                                </TableContainer.TableValue>

                                <TableContainer.Action
                                    icon={<RiDeleteBinLine />}
                                    altText="Delete Alias"
                                    onClick={column => {
                                        setDeleting(column);
                                    }}
                                    canClick={column => !column.isDefault}
                                />

                            </TableContainer.Table>
                        </TableContainer>
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>

                {
                    viewWorkflowId && (
                        <ViewAutomation
                            settings={props.settings}
                            workflowId={viewWorkflowId}
                            onHide={() => {
                                setViewWorkflowId(undefined);
                            }}
                            startTab={AutomationTab.GENERAL_DETAILS}
                            form={{
                                formId: undefined,
                                formType: WorkflowTriggerType.Email,
                            }}
                        />
                    )
                }
            </SettingsContainer>
            {deleteElement}
            {saveElement}
        </>

    );
}
